import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'common/components/modal';
import ModalBody from 'common/components/modal-body';
import ModalFooter from 'common/components/modal-footer';
import ModalHeader from 'common/components/modal-header';
import OptionList from 'common/components/option-list';
import Select from 'common/components/select';
import {t} from 'common/utils/translation-util';
import {sort} from 'common/utils/array-util';
import StoryboardChapterIcon from 'storyboard/components/storyboard-chapter-icon';
import * as ContentTitleOption from 'storyboard/constants/content-title-option';
import * as ModalType from 'storyboard/constants/modal-type';
import './index.scss';

export default class StoryboardChapterModal extends React.Component {
  static propTypes = {
    addChapter: PropTypes.func,
    chapterToEdit: PropTypes.object,
    closeModal: PropTypes.func,
    dashboards: PropTypes.array.isRequired,
    data: PropTypes.object.isRequired,
    deleteChapter: PropTypes.func,
    editChapter: PropTypes.func,
    enums: PropTypes.object.isRequired,
    isOpen: PropTypes.bool,
    title: PropTypes.string,
    type: PropTypes.string,
  };

  static defaultProps = {
    addChapter: () => {},
    chapterToEdit: null,
    closeModal: () => {},
    deleteChapter: () => {},
    editChapter: () => {},
    isOpen: false,
    title: '',
    type: '',
  };

  constructor() {
    super();
    this.handleCancel = this.handleCancel.bind(this);
    this.handleChapterTitleChange = this.handleChapterTitleChange.bind(this);
    this.handleDashboardChange = this.handleDashboardChange.bind(this);
    this.handleIconChange = this.handleIconChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleShowTitleChange = this.handleShowTitleChange.bind(this);
    this.setDefaultChapter = this.setDefaultChapter.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.setDefaultChapter(this.props.data, this.props.enums);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setContentOptions(nextProps.dashboards);
    this.setIconOptions(nextProps.enums);

    const contentId = this.getContentId(this.contentOptions);
    this.setChapterToEdit(nextProps.chapterToEdit, contentId);
  }

  getContentId(contentOptions) {
    return contentOptions && contentOptions[0] ? contentOptions[0].value : null;
  }

  setChapterToEdit(chapterToEdit, contentId) {
    if (chapterToEdit) {
      // If chapterToEdit is truthy, the modal is
      // intended to edit this existing chapter.
      this.setState({
        contentId: chapterToEdit.contentId,
        icon: chapterToEdit.icon.id,
        id: chapterToEdit.id,
        position: chapterToEdit.position,
        showTitle: chapterToEdit.showTitle,
        title: chapterToEdit.title,
      });
    } else {
      // If chapterToEdit is falsy, the modal is
      // intended to create a new chapter. Use
      // the attributes already on component
      // state, but update the content ID so that
      // the dashboard select has its first
      // option selected by default.
      this.setState({
        contentId,
      });
    }
  }

  setContentOptions(dashboards) {
    this.contentOptions = dashboards.map((dashboard) => ({
      label: dashboard.name,
      value: dashboard.id,
    }));
  }

  setDefaultChapter(data, enums) {
    const {ChapterIconEnum} = enums;
    this.setState({
      // We don't know the value of content ID yet.
      // This gets set later by setChapterToEdit().
      contentId: null,

      // FIXME
      // Behold a magic number par excellence.
      // The posted content type needs to correspond
      // to a Django content type, in this case 76.
      // This relates to ApplicationType.REPORT_DASHBOARD.
      // But right now, there's no easy way to get this
      // through to JS. Also right now, we only have this
      // one content type. So in order not to vortex on
      // an extremely annoying problem, we hardcode the
      // number and move on, knowing we'll have to return
      // to it later.
      contentType: 76,

      icon: ChapterIconEnum.BUILDINGS.id,
      position: data.chapters.length,
      showTitle: false,
      title: '',
    });
  }

  setIconOptions(enums) {
    const {ChapterIconEnum} = enums;
    this.iconOptions = Object.keys(ChapterIconEnum)
      .map((key) => ({
        icon: (
          <StoryboardChapterIcon
            style={{
              height: '24px',
              margin: 0,
              padding: 0,
              position: 'absolute',
              width: '24px',
            }}
            type={ChapterIconEnum[key].id}
          />
        ),
        label: ChapterIconEnum[key].displayName,
        value: ChapterIconEnum[key].id,
      }))
      .filter(
        (icon) =>
          // The "Add a chapter" icon should not be
          // available to users, so filter it out:
          icon.value !== ChapterIconEnum.ADD_CHAPTER.id,
      );

    // sort icon options alphabetically for easier UI viewing
    this.iconOptions = sort.alphabetical(this.iconOptions, 'label');
  }

  render() {
    return (
      <Modal className={'StoryboardChapterModal'} isOpen={this.props.isOpen}>
        <ModalHeader>{this.props.title}</ModalHeader>

        {this.props.type === ModalType.DELETE_CHAPTER ? (
          <ModalBody>
            <p>{t('Are you sure you want to delete this chapter?')}</p>
          </ModalBody>
        ) : (
          <ModalBody>
            <h4>{t('Chapter Title')}</h4>
            <input
              onChange={this.handleChapterTitleChange}
              type={'text'}
              value={this.state.title}
            />

            <h4>{t('Icon')}</h4>
            <Select
              onChange={this.handleIconChange}
              options={this.iconOptions || []}
              value={this.state.icon}
            />

            <h4>{t('Dashboard')}</h4>
            <Select
              onChange={this.handleDashboardChange}
              options={this.contentOptions || []}
              value={this.state.contentId}
            />

            <h4>{t('Dashboard Title')}</h4>
            <OptionList
              onChange={this.handleShowTitleChange}
              options={[
                {
                  label: ContentTitleOption.HIDE.label,
                  value: ContentTitleOption.HIDE.value,
                },
                {
                  label: ContentTitleOption.SHOW.label,
                  value: ContentTitleOption.SHOW.value,
                },
              ]}
              value={this.state.showTitle}
            />
          </ModalBody>
        )}

        {this.props.type === ModalType.DELETE_CHAPTER ? (
          <ModalFooter
            onCancel={this.handleCancel}
            onSave={this.handleSave}
            saveButtonProps={{
              children: t('Delete'),
              type: 'red',
            }}
          />
        ) : (
          <ModalFooter onCancel={this.handleCancel} onSave={this.handleSave} />
        )}
      </Modal>
    );
  }

  handleCancel() {
    this.props.closeModal();
    this.setDefaultChapter(this.props.data, this.props.enums);
  }

  handleChapterTitleChange(event) {
    this.setState({
      title: event.target.value,
    });
  }

  handleDashboardChange(value) {
    this.setState({
      contentId: value,
    });
  }

  handleIconChange(value) {
    this.setState({
      icon: value,
    });
  }

  handleSave() {
    switch (this.props.type) {
      case ModalType.ADD_CHAPTER:
        this.props.addChapter(this.state);
        break;
      case ModalType.DELETE_CHAPTER:
        this.props.deleteChapter(this.state);
        break;
      case ModalType.EDIT_CHAPTER:
        this.props.editChapter(this.state);
        break;
      default:
      // Do nothing
    }
    this.setDefaultChapter(this.props.data, this.props.enums);
    this.props.closeModal();
  }

  handleShowTitleChange(value) {
    this.setState({
      showTitle: value,
    });
  }
}
